import ProfileSettings from "@assets/svg_icons/profile_settings"
import { AccordionProps } from "@components/accordion_group"
import {
  AppContextProps,
  appContextDefaultValues,
  useAppContext
} from "@contexts/app_context"
import useToggle from "@hooks/use_toggle"
import i18next from "@utils/localization"
import { createContext, useContext, useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"

const emptyProfileData = {
  firstName: "",
  lastName: "",
  language: "",
  email: "",
  phone: {
    value: "",
    dialCode: "",
    countryCode: ""
  }
} as AccountPageContextProps["profileData"]

export type AccountPageContextProps = AppContextProps & {
  isLeftDrawerOpen?: boolean
  toggleIsLeftDrawerOpen?: () => void
  navigationValue?: string
  handleClickNavigationRow?: (value: string) => void
  navigationColumns: AccordionProps[]
  profileData: {
    firstName: string
    lastName: string
    email: string
    language: string
    role?: string
    phone: {
      value: string
      dialCode: string
      countryCode: string
    }
  }
  setProfileData?: (profileData: AccountPageContextProps["profileData"]) => void
}

export const accountPageDefaultValues: AccountPageContextProps = {
  ...appContextDefaultValues,
  translate: i18next.t,
  navigationColumns: [],
  profileData: emptyProfileData
}

const AccountPageContext = createContext(accountPageDefaultValues)

export function AccountPageContextProvider(props: {
  children: React.ReactNode | React.ReactNode[]
}) {
  const { translate, me } = useAppContext()
  const [isLeftDrawerOpen, toggleIsLeftDrawerOpen] = useToggle(true)
  const [navigationValue, setNavigationValue] = useState(
    window.location.pathname
  )
  const navigate = useNavigate()
  const location = useLocation()
  const [profileData, setProfileData] =
    useState<AccountPageContextProps["profileData"]>(emptyProfileData)

  function handleClickNavigationRow(value: string) {
    setNavigationValue(value)
    navigate(value)
  }

  useEffect(() => {
    if (navigationValue !== location.pathname) {
      setNavigationValue(location.pathname)
    }
  }, [location.pathname, navigationValue])

  const navigationColumns: AccordionProps[] = [
    {
      title: translate("your_profile"),
      isDefaultOpen: false,
      rowList: [
        {
          label: translate("profile"),
          value: "/account/profile",
          Icon: <ProfileSettings />
        },
        {
          label: translate("password"),
          value: "/account/profile/password",
          Icon: <ProfileSettings />
        }
      ]
    }
  ]

  return (
    <AccountPageContext.Provider
      value={{
        ...accountPageDefaultValues,
        translate,
        me,
        isLeftDrawerOpen,
        toggleIsLeftDrawerOpen,
        navigationValue,
        handleClickNavigationRow,
        navigationColumns,
        profileData,
        setProfileData
      }}
    >
      {props.children}
    </AccountPageContext.Provider>
  )
}

export function useAccountPageContext() {
  const context = useContext(AccountPageContext)
  if (context === undefined) {
    throw new Error(
      "useAccountPageContext must be used within an AccountPageContextProvider"
    )
  }
  return context
}
